/**
 * Seed Stage — Stage 5b: Seed integrity & completeness checks.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.10 + Strak-Trek Phase 2
 *
 * Two responsibilities:
 *
 * 1. Consolidation warning surfacing (legacy)
 *    The seedConsolidator auto-corrects tier/skill/subSkill mismatches at
 *    load time. This stage surfaces those corrections as proper
 *    ValidationIssues so they appear in the governance UI.
 *
 * 2. Seed completeness (Strak-Trek Phase 2)
 *    Seeds MUST declare `pedagogy.dok`. When it's missing the stage emits
 *    a single SEED_DOK_MISSING issue (blocker when
 *    `enforcePedagogicalContract` flag is on, coach otherwise). The
 *    dokAutoTagger heuristic estimate is folded into that issue's
 *    `metadata` field — admin UI reads it to offer "apply suggestion"
 *    actions without a parallel info issue. (Previously emitted as a
 *    separate SEED_DOK_AUTO_ESTIMATED info issue; consolidated to remove
 *    the 1-to-1 duplicate.)
 *
 * Issue kinds:
 * - SEED_TIER_AUTOCORRECTED: blocker — tier in seed JSON doesn't match taxonomy
 * - SEED_TIER_INVALID:       blocker — tier was missing or non-numeric
 * - SEED_TIER_OUT_OF_RANGE:  blocker — tier was > 16
 * - SEED_SKILL_AUTOCORRECTED:    coach — skill didn't match taxonomy
 * - SEED_SUBSKILL_AUTOCORRECTED: coach — subSkill didn't match taxonomy
 * - SEED_DOK_MISSING:        blocker/coach — pedagogy.dok missing in seed
 *                            (carries heuristic estimate in metadata)
 */

import type { Question } from '../../../content'
import type { ValidationIssue } from '../issueContract'
import { blocker, coach, ISSUE_CODES } from '../issueContract'
import { getConsolidationReport } from '../../../content'
import { isPolicyFeatureEnabled } from '../policyEngine'
import { estimateDok } from '../../utils/dokAutoTagger'
import { SEVERITY } from '../severityScale'
import { STAGES } from '../stageDefinitions'

// SSOT for stage number — see stageDefinitions.ts. seedStage runs as Stage 5b.
const STAGE = STAGES.SEED

/**
 * Tier cutoff above which a missing-DOK coach issue escalates from
 * SEVERITY.LOW to 50. Tier ≥ 5 lives in the "instap-hard" / "uitdaging" /
 * "plus" bands where seeds are higher-stakes and authors should be more
 * urgently nudged to set DOK.
 */
const DOK_MISSING_COACH_ESCALATION_TIER = 5

// ─── Cache ────────────────────────────────────────────────────────────────────

let _cachedWarningMap: Map<string, ValidationIssue[]> | null = null

/**
 * Invalidate the cached warning map (for tests or hot-reload).
 */
export function invalidateSeedWarningCache(): void {
  _cachedWarningMap = null
}

// ─── Warning Parser ───────────────────────────────────────────────────────────

/**
 * Parse consolidation warnings into a map of questionId → ValidationIssue[].
 * Cached after first call.
 */
function getWarningMap(): Map<string, ValidationIssue[]> {
  if (_cachedWarningMap) return _cachedWarningMap

  const map = new Map<string, ValidationIssue[]>()
  const report = getConsolidationReport()

  for (const warning of report.warnings) {
    const parsed = parseWarning(warning)
    if (!parsed) continue

    const existing = map.get(parsed.questionId) || []
    existing.push(parsed.issue)
    map.set(parsed.questionId, existing)
  }

  _cachedWarningMap = map
  return map
}

interface ParsedWarning {
  questionId: string
  issue: ValidationIssue
}

/**
 * Parse a single consolidation warning string into a ValidationIssue.
 *
 * Warning formats from seedConsolidator:
 * - "SEED TIER-LOCK: Question {id} has invalid tier ({rawTier}), auto-corrected to taxonomy tier {taxonomyTier} for family {familyId}"
 * - "SEED TIER-LOCK: Question {id} has out-of-range tier {rawTier}, auto-corrected to taxonomy tier {taxonomyTier} for family {familyId}"
 * - "SEED TIER-LOCK: Question {id} (family {familyId}) has tier {rawTier} but taxonomy defines tier {taxonomyTier}. Auto-corrected to {taxonomyTier}."
 * - "SEED SKILL-LOCK: Question {id} (family {familyId}) has skill "{skill}" but taxonomy defines "{taxonomySkill}". Auto-corrected."
 * - "SEED SUBSKILL-LOCK: Question {id} (family {familyId}) has subSkill "{subSkill}" but taxonomy defines "{taxonomySubSkill}". Auto-corrected."
 */
function parseWarning(warning: string): ParsedWarning | null {
  // ── TIER-LOCK: invalid tier ──
  const invalidTierMatch = warning.match(
    /SEED TIER-LOCK: Question (\S+) has invalid tier \(([^)]*)\), auto-corrected to taxonomy tier (\d+) for family (\S+)/,
  )
  if (invalidTierMatch) {
    const [, questionId, rawTier, taxonomyTier, familyId] = invalidTierMatch
    return {
      questionId: questionId!,
      issue: blocker('seed', STAGE, 'SEED_TIER_INVALID', warning, {
        field: 'tier',
        expected: taxonomyTier,
        actual: rawTier,
        questionId: questionId!,
        familyId: familyId!,
        suggestion:
          'Fix the tier value in the seed JSON to match the taxonomy definition',
      }),
    }
  }

  // ── TIER-LOCK: out-of-range ──
  const outOfRangeMatch = warning.match(
    /SEED TIER-LOCK: Question (\S+) has out-of-range tier (\d+), auto-corrected to taxonomy tier (\d+) for family (\S+)/,
  )
  if (outOfRangeMatch) {
    const [, questionId, rawTier, taxonomyTier, familyId] = outOfRangeMatch
    return {
      questionId: questionId!,
      issue: blocker('seed', STAGE, 'SEED_TIER_OUT_OF_RANGE', warning, {
        field: 'tier',
        expected: taxonomyTier,
        actual: rawTier,
        questionId: questionId!,
        familyId: familyId!,
        suggestion: 'Tier must be 1-16. Fix the tier value in the seed JSON.',
      }),
    }
  }

  // ── TIER-LOCK: mismatch ──
  const tierMismatchMatch = warning.match(
    /SEED TIER-LOCK: Question (\S+) \(family (\S+)\) has tier (\d+) but taxonomy defines tier (\d+)/,
  )
  if (tierMismatchMatch) {
    const [, questionId, familyId, rawTier, taxonomyTier] = tierMismatchMatch
    return {
      questionId: questionId!,
      issue: blocker(
        'seed',
        STAGE,
        'SEED_TIER_AUTOCORRECTED',
        `Seed has tier ${rawTier} but taxonomy defines tier ${taxonomyTier} — auto-corrected at load time`,
        {
          field: 'tier',
          expected: taxonomyTier,
          actual: rawTier,
          questionId: questionId!,
          familyId: familyId!,
          suggestion: `Update tier from ${rawTier} to ${taxonomyTier} in the seed JSON to fix this permanently`,
        },
      ),
    }
  }

  // ── SKILL-LOCK ──
  const skillMatch = warning.match(
    /SEED SKILL-LOCK: Question (\S+) \(family (\S+)\) has skill "([^"]+)" but taxonomy defines "([^"]+)"/,
  )
  if (skillMatch) {
    const [, questionId, familyId, rawSkill, taxonomySkill] = skillMatch
    return {
      questionId: questionId!,
      issue: coach(
        'seed',
        STAGE,
        'SEED_SKILL_AUTOCORRECTED',
        `Seed has skill "${rawSkill}" but taxonomy defines "${taxonomySkill}" — auto-corrected at load time`,
        {
          field: 'skill',
          expected: taxonomySkill,
          actual: rawSkill,
          questionId: questionId!,
          familyId: familyId!,
          severity: SEVERITY.NEAR_HIGH,
          suggestion: `Update skill from "${rawSkill}" to "${taxonomySkill}" in the seed JSON`,
        },
      ),
    }
  }

  // ── SUBSKILL-LOCK ──
  const subSkillMatch = warning.match(
    /SEED SUBSKILL-LOCK: Question (\S+) \(family (\S+)\) has subSkill "([^"]+)" but taxonomy defines "([^"]+)"/,
  )
  if (subSkillMatch) {
    const [, questionId, familyId, rawSubSkill, taxonomySubSkill] =
      subSkillMatch
    return {
      questionId: questionId!,
      issue: coach(
        'seed',
        STAGE,
        'SEED_SUBSKILL_AUTOCORRECTED',
        `Seed has subSkill "${rawSubSkill}" but taxonomy defines "${taxonomySubSkill}" — auto-corrected at load time`,
        {
          field: 'subSkill',
          expected: taxonomySubSkill,
          actual: rawSubSkill,
          questionId: questionId!,
          familyId: familyId!,
          severity: SEVERITY.REVIEW,
          suggestion: `Update subSkill from "${rawSubSkill}" to "${taxonomySubSkill}" in the seed JSON`,
        },
      ),
    }
  }

  return null
}

// ─── Phase 2: Seed DOK Completeness Check ────────────────────────────────────

/**
 * Phase 2 (Strak-Trek) — seeds MUST declare `pedagogy.dok`.
 *
 * Runs for every question, all tiers. When the field is missing emits a
 * single SEED_DOK_MISSING issue:
 *  - Blocker when `enforcePedagogicalContract` feature flag is on; coach
 *    otherwise (severity scales gently by tier so high-stakes content is
 *    more urgent in the admin UI).
 *  - Runs the dokAutoTagger heuristic and folds the result into the
 *    issue's `suggestion` string (Dutch UX text) AND its `metadata`
 *    payload ({ estimatedDok, confidence, reason, heuristicVersion }) so
 *    the admin UI can render a one-click "apply suggestion" action
 *    without a parallel info issue.
 *
 * Defined-DOK alignment (floor/ceiling, too-high) stays in pedagogyStage —
 * this stage only owns the completeness gate.
 */
function checkSeedDokMissing(question: Question): ValidationIssue[] {
  if (question.pedagogy?.dok !== undefined) return []

  const enforce = isPolicyFeatureEnabled('enforcePedagogicalContract')
  const tier = question.tier ?? 1

  // Run the heuristic estimator for a content-author suggestion + metadata.
  let suggestion =
    'Voeg een pedagogy.dok waarde (1-4) toe aan de seed-data. DOK is verplicht voor alle vragen.'
  let estimate: ReturnType<typeof estimateDok> | null = null
  try {
    estimate = estimateDok(question)
    suggestion =
      `Geschatte DOK: ${estimate.dok} (heuristisch, confidence: ${estimate.confidence}). ` +
      `Reden: ${estimate.reason}. ` +
      `Voeg een pedagogy.dok waarde (1-4) toe aan de seed-data.`
  } catch {
    // Heuristic failed — fall back to plain suggestion.
  }

  // Severity tier-scaling (gentle):
  //  - blocker path: 75 across the board (matches former PEDAGOGY_DOK_MISSING enforce)
  //  - coach path:   50 for tier >= DOK_MISSING_COACH_ESCALATION_TIER,
  //                  SEVERITY.LOW (35) otherwise
  const coachSeverity =
    tier >= DOK_MISSING_COACH_ESCALATION_TIER ? 50 : SEVERITY.LOW

  // Heuristic estimate folded into metadata (was previously a separate
  // SEED_DOK_AUTO_ESTIMATED info issue — now a single signal).
  const metadata: Record<string, unknown> | undefined = estimate
    ? {
        estimatedDok: estimate.dok,
        confidence: estimate.confidence,
        reason: estimate.reason,
        heuristicVersion: 1,
      }
    : undefined

  const mainIssue = enforce
    ? blocker(
        'seed',
        STAGE,
        ISSUE_CODES.SEED_DOK_MISSING,
        `Vraag ${question.id} mist verplichte pedagogy.dok (DOK-niveau).`,
        {
          questionId: question.id,
          familyId: question.familyId,
          field: 'pedagogy.dok',
          expected: 'DOK 1-4',
          actual: 'ontbreekt',
          suggestion,
          severity: SEVERITY.NEAR_CRITICAL,
          metadata,
        },
      )
    : coach(
        'seed',
        STAGE,
        ISSUE_CODES.SEED_DOK_MISSING,
        `Vraag ${question.id} mist verplichte pedagogy.dok (DOK-niveau).`,
        {
          questionId: question.id,
          familyId: question.familyId,
          field: 'pedagogy.dok',
          expected: 'DOK 1-4',
          actual: 'ontbreekt',
          suggestion,
          severity: coachSeverity,
          metadata,
        },
      )

  return [mainIssue]
}

// ─── Stage Entry Point ────────────────────────────────────────────────────────

/**
 * Run seed-stage checks for a single question:
 *  1. Surface consolidation warnings (tier/skill/subSkill auto-corrections).
 *  2. Phase 2 — enforce `pedagogy.dok` completeness.
 */
export function runSeedStage(question: Question): ValidationIssue[] {
  const qId = question.id ?? 'UNKNOWN'
  const warningMap = getWarningMap()
  const consolidationIssues = warningMap.get(qId) ?? []
  const dokIssues = checkSeedDokMissing(question)
  return [...consolidationIssues, ...dokIssues]
}
